import { useCallback, useEffect, useState } from "react";
import { debounce } from "lodash";
import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { apiClient } from "../../shared/customHooks/hooks/useApiClient";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";

export interface rowColumn {
  label: string;
  column?: string;
  type?: "string" | "number" | "date" | "dateTime" | "enum" | "boolean";
  enumType?: any; //enum in here for value matching
  allowSort?: boolean;
  disableSearch?: boolean;
  sortOn?: string;
  isVisible?: boolean;
  isFilter?: boolean;
  filterElements?: { label: string; value: any }[]; // column filter
  filterChange?: (event: any) => void; // New property for filter change handler
  body?: (rowData: any) => React.JSX.Element | any;
  globalFilterColumn?: boolean;
  globalfilterElements?: { label: string; value: any }[]; // global
  filterKeyColumns?: string[];
  filterType?: string;
}

export interface tableOptions {
  take?: number;
  sortDirection?: "asc" | "desc";
  searchColumns?: string[];
  tableColumns?: string[];
  skip?: number;
  sortColumn?: string;
  sortField: string | undefined;
  sortOrder: -1 | 0 | 1 | undefined;
  filterSearchColumns?: string[];
  filters?: {
    [key: string]: filterHelper;
  };
}

interface tableProps {
  apiClient: apiClient;
  label?: string;
  columns: rowColumn[];
  tableOptions?: tableOptions;
  route?: string;
}

interface filterHelper {
  searchFilter?: any;
  exclude?: boolean;
  filterSearchColumns?: string[];
}

export interface filterProps {
  column?: rowColumn;
  columnKey?: string;
  filterColumns?: string[];
  filterType?: FilterType | null;
  setterFn?: { (value: React.SetStateAction<any[]>): void; (arg0: any): void };
}

enum FilterType {
  Search = "Search",
  MultiSelect = "MultiSelect",
}

export default function Table({ apiClient, label, columns, tableOptions, route }: tableProps) {
  const [options, setOptions] = useState<tableOptions>({
    take: 10,
    sortDirection: tableOptions?.sortDirection ?? "desc",
    skip: 0,
    sortColumn: tableOptions?.sortColumn ?? "DateCreated",
    sortOrder: undefined,
    sortField: undefined,
  });
  const [tableData, setTableData] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<{ [key: string]: any }>({});
  const [selectedGenricFilter, setSelectedGenericFilter] = useState<{ [key: string]: any }>({});
  const globalFilter = columns.find((x) => x.globalFilterColumn);
  const isFilter = columns.find((x) => x.isFilter);

  const refreshTable = (newOptions: tableOptions, newSearch?: string) => {
    const payload = { ...newOptions, search: newSearch ?? search };
    apiClient.postData(payload, route ?? "all").then((res) => {
      setOptions(newOptions);

      setTableData(res.data.data);
      setTotalResults(res.data.totalResults);
    });
  };

  useEffect(() => {
    const searchColumns = columns.filter((c) => c.sortOn != null && !c.disableSearch).map((c) => c.sortOn);
    const tableColumns = columns.filter((c) => c.sortOn != null).map((c) => c.sortOn);
    const currentTableOptions = structuredClone(tableOptions);
    const initOptions = { ...options, ...currentTableOptions };
    initOptions.searchColumns = searchColumns as string[];
    initOptions.tableColumns = tableColumns as string[];
    refreshTable(initOptions);
  }, []);

  const debouncedFetchSearchResults = useCallback(
    debounce(async (searchQuery: any, searchFilter: any) => {
      const o = searchFilter != undefined ? searchFilter : { ...options };
      o.skip = 0;
      refreshTable(o, searchQuery);
    }, 1000),
    [options] // Add stable dependencies
  );
  const handleSearchChange = (e: any) => {
    const newQuery = e.target.value;
    setSearch(newQuery);
    if (newQuery.length >= 3 || newQuery.length == 0) {
      debouncedFetchSearchResults(newQuery, null);
    }
  };

  useEffect(() => {
    return () => {
      debouncedFetchSearchResults.cancel(); // Cleanup on unmount
    };
  }, [debouncedFetchSearchResults]);

  function sort(event: any) {
    const col = columns.find((x) => x.column == event.sortField);
    const o = { ...options };
    o.skip = 0;
    if (o.sortColumn != col?.sortOn) {
      o!.sortColumn = col?.sortOn;
    } else {
      o.sortDirection = o.sortDirection == "asc" ? "desc" : "asc";
    }
    o.sortField = event.sortField;
    o.sortOrder = event.sortOrder;
    refreshTable(o);
  }

  function onPage(event: any) {
    const newOptions = { ...options };
    newOptions.skip = event.first;
    newOptions.take = event.rows;
    refreshTable(newOptions);
  }

  const onFilterChange = (e: any, filterProp: filterProps) => {
    const columnKey = filterProp.columnKey != null ? filterProp.columnKey : String(filterProp.column?.column);
    const value = e.value != undefined ? e.value : e.target.value;
    if (value.length > 0 || value == -1) {
      const o = { ...options };
      o.skip = 0;

      // Create a copy of o.filters and make sure updatedFilters is initialized properly
      const updatedFilters = { ...o.filters };
      // Ensure columnKey exists in the updatedFilters object
      if (!updatedFilters[columnKey]) {
        updatedFilters[columnKey] = { searchFilter: "", filterSearchColumns: [] }; // Initialize if not already present
      }
      // Update the searchFilter and filterSearchColumns
      updatedFilters[columnKey].searchFilter = value;
      updatedFilters[columnKey].filterSearchColumns = filterProp.filterColumns != null ? filterProp.filterColumns : undefined;

      // Reassign the updated filters back to o.filters
      o.filters = { ...o.filters, ...updatedFilters };

      // set values
      filterProp.setterFn?.((prev: any) => ({
        ...prev,
        [columnKey]: value, // Array of selected values
      }));

      if (filterProp.filterType == FilterType.Search) {
        debouncedFetchSearchResults("", o);
      } else {
        refreshTable(o);
      }
    } else {
      clearFilter(columnKey);
    }
  };

  const clearFilter = (columnKey: string | "") => {
    const o = { ...options };
    o.skip = 0;

    if (columnKey != "") {
      if (o.filters) {
        delete o.filters[columnKey];
      }
      setSelectedGenericFilter((prev: any) => {
        const updated = { ...prev };
        delete updated[columnKey]; // Remove just the key
        return updated;
      });
    } else {
      if (o.filters) {
        delete o.filters;
      }
      setSelectedGenericFilter([]);
    }

    const passedTableOptions = structuredClone(tableOptions);
    const defaultTableOptions = { ...o, ...passedTableOptions };
    refreshTable(defaultTableOptions);
  };

  function Body(rowData: any, col: rowColumn) {
    //DO NOT POLUTE WITH SPECIFIC FUNCTIONALITY, if you cant make it generic, use the body callback
    if (col.body) {
      return col.body(rowData);
    }
    const result = col.column!.split(".").reduce((previous, current) => previous && previous[current], rowData);
    if (result == null) return;
    if (col.type === "date") {
      const dateValue = col.column!.includes(".") ? new Date(result) : new Date(rowData[col.column ?? ""]);
      const formattedDate =
        ("0" + dateValue.getDate()).slice(-2) + "/" + ("0" + (dateValue.getMonth() + 1)).slice(-2) + "/" + dateValue.getFullYear();
      return formattedDate;
    }
    if (col.type === "dateTime") {
      const dateValue = col.column!.includes(".") ? new Date(result) : new Date(rowData[col.column ?? ""]);
      dateValue.setHours(dateValue.getHours() - dateValue.getTimezoneOffset() / 60);
      let formattedDate = dateValue.toLocaleDateString("en-GB");
      formattedDate += " " + dateValue.toLocaleString("en-CA", { timeStyle: "medium", hour12: false });

      return formattedDate;
    }
    if (col.type == "enum") {
      const enumVal = col.enumType[result];
      const friendlyEnum = enumVal
        .split(/(?=[A-Z])/) // Split before each uppercase letter
        .join(" ");
      return friendlyEnum;
    } else {
      return result;
    }
  }

  // Custom filter element logic
  const customFilterElement = (column: rowColumn) => {
    const currentFilter = column.column && selectedGenricFilter[column.column];
    switch (column.filterType) {
      case "multiselect":
        return (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", marginBottom: "1rem" }}>
              <i className={currentFilter ? "pi p-3 relative pi-filter-fill text-purple" : "pi pi-filter p-3 relative text-purple"}></i>
              <MultiSelect
                selectAllLabel="Select all"
                style={{ borderRadius: "0px", marginLeft: "-40px", border: "2px solid var(--primary-color-purple)", paddingLeft: "20px" }}
                value={currentFilter || []}
                options={Array.isArray(column.filterElements) ? column.filterElements : []}
                className={currentFilter && column.isFilter ? "filterIcon p-column-filter" : "noFilter"}
                onChange={(e: any) => {
                  const prop: filterProps = {
                    column: column,
                    setterFn: setSelectedGenericFilter,
                    filterType: null,
                  };
                  onFilterChange(e, prop);
                }}
                placeholder="Filter Options"
                title={currentFilter?.length ? `${currentFilter.length} items selected` : "Filter Options"}
                maxSelectedLabels={0}
              />
            </div>
          </>
        );
      case "search":
        return (
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search mt-4 ml-2" aria-hidden="true">
              {" "}
            </InputIcon>
            <InputText
              style={{ border: "2px solid var(--primary-color-purple)", background: "#fff" }}
              placeholder="Search"
              value={currentFilter || ""}
              onChange={(e: any) => {
                const prop: filterProps = {
                  column: column,
                  filterColumns: column.filterKeyColumns,
                  filterType: FilterType.Search,
                  setterFn: setSelectedGenericFilter,
                };
                onFilterChange(e, prop);
              }}
            />
          </IconField>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <h1>{label}</h1>
      <div className="flex items-center mb-9">
        <div className="flex flex-row w-full">
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search mt-3 ml-2" aria-hidden="true">
              {" "}
            </InputIcon>
            <InputText placeholder="Search" name="search" value={search} onChange={handleSearchChange} />
          </IconField>
        </div>
        {/*Global filter - pass property from list table
            globalFilterColumn: true,
            globalfilterElements: getFilterOptions(assignStatusPrimaryOptions, assignStatusSecondaryOptions), */}
        <div className="filter">
          {/* Top-right filter controls */}
          {globalFilter != undefined && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: "1rem" }}>
              <i className={selectedFilter.length > 0 ? "pi p-3 relative pi-filter-fill text-purple" : "pi pi-filter p-3 relative text-purple"}></i>
              <MultiSelect
                selectAllLabel="Select all"
                style={{ borderRadius: "0px", border: "2px solid var(--primary-color-purple)", paddingLeft: "30px" }}
                value={(globalFilter.column && selectedFilter[globalFilter.column]) || []}
                options={Array.isArray(globalFilter.globalfilterElements) ? globalFilter.globalfilterElements : []}
                onChange={(e: any) => {
                  if (globalFilter != null) {
                    const prop: filterProps = {
                      columnKey: globalFilter.column,
                      setterFn: setSelectedFilter,
                    };
                    onFilterChange(e, prop);
                  }
                }}
                placeholder="Filter Options"
                title="Filter Options"
                maxSelectedLabels={4}
              />
            </div>
          )}
          {isFilter && (
            <Button
              icon="pi pi-times-circle"
              disabled={Object.keys(selectedGenricFilter).length === 0}
              className="whitespace-nowrap rounded-none! clear-filter"
              label="Clear Filter"
              onClick={() => clearFilter("")}
              severity="secondary"
            />
          )}
        </div>
      </div>
      <DataTable
        paginator
        filterDisplay={isFilter ? "row" : undefined}
        lazy
        onSort={sort}
        value={tableData}
        totalRecords={totalResults}
        footer={"Total number of entries: " + totalResults}
        rows={options.take}
        onPage={onPage}
        first={options.skip}
        sortField={options.sortField}
        sortOrder={options.sortOrder}
        rowsPerPageOptions={[10, 25, 50]}
        emptyMessage="No entries found"
        className="mt-5 records-table"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} results"
        tableStyle={{ minWidth: "50rem" }}>
        {columns.map(
          (col, i) =>
            col.isVisible != false && (
              <Column
                key={`${col.column || "col"}-${i}`}
                field={col.column}
                header={col.label}
                sortable={col.allowSort}
                body={(rowData) => Body(rowData, col)}
                filter={col.isFilter}
                showClearButton={false}
                showApplyButton={false}
                showAddButton={false}
                showFilterMenu={false}
                showFilterMatchModes={false}
                showFilterMenuOptions={false}
                filterElement={col.isFilter != undefined && col.isFilter && customFilterElement(col)}
              />
            )
        )}
      </DataTable>
    </>
  );
}
